// Pure CSV helper functions for the place-selection CSV upload flow.
//
// These are intentionally free of any Vue/component dependency (and any
// third-party library) so that fast-check property tests can import and
// exercise them directly. They mirror the extraction pattern used by
// placeSelectionDrawHelpers.js in this same folder.
// Feature: csv-place-upload (Task 1)

// Radius primitives are shared with the Places Action Modal point-to-area
// conversion. They live in radiusGeometry.js (also pure/turf-free) and are
// re-exported here so existing importers of placeSelectionCsv keep working.
// Feature: convert-point-places-to-radius
import { radiusToKm, squarePolygonFor } from "./radiusGeometry";

export { radiusToKm, squarePolygonFor };

/**
 * Maximum number of valid CSV rows supported for a single upload (Row_Cap).
 * @type {number}
 */
export const CSV_ROW_CAP = 10000;

/**
 * Maximum number of rows rendered in the tab list and as map markers for a
 * CSV upload preview (Preview_Cap).
 * @type {number}
 */
export const CSV_PREVIEW_CAP = 500;

/**
 * Client-side pre-parse guard on the raw CSV file size, in bytes (5 MB).
 * @type {number}
 */
export const CSV_MAX_BYTES = 5 * 1024 * 1024;

/**
 * A single parsed, valid CSV row.
 *
 * Latitude/longitude are optional: when a row omits them (or leaves them
 * blank) the Places API derives a coordinate from `address` via geocoding, so
 * `lat`/`lon` are `null` on such rows.
 *
 * @typedef {Object} CsvRow
 * @property {string} name         Column 1: the place name.
 * @property {string} address      Column 2: the place address.
 * @property {number|null} lat     Column 3: latitude in [-90, 90], or null when omitted (geocoded from address).
 * @property {number|null} lon     Column 4: longitude in [-180, 180], or null when omitted (geocoded from address).
 */

/**
 * The result of parsing raw CSV text.
 *
 * @typedef {Object} ParseResult
 * @property {CsvRow[]} valid         Valid rows only, in source order.
 * @property {number}   total         Count of parseable (non-blank) lines considered.
 * @property {number}   validCount    Number of valid rows (equals `valid.length`).
 * @property {number}   excludedCount Number of malformed + invalid rows (`total - validCount`).
 * @property {string|null} error      Set when there are zero valid rows OR the row cap is exceeded; otherwise null.
 */

/**
 * Split a single CSV line into fields.
 *
 * Honors comma as the field separator, double-quote-wrapped fields (with `""`
 * as an escaped quote inside a quoted field), and trims leading/trailing
 * whitespace from unquoted fields while preserving whitespace inside quoted
 * fields.
 *
 * NOTE: Implementation is added in a later task (Task 2.1).
 *
 * @param {string} line - A single line of CSV text.
 * @returns {string[]} The parsed fields for the line.
 */
export function splitCsvLine(line) {
  const fields = [];
  const text = line == null ? "" : String(line);

  let current = "";
  let inQuotes = false;
  let wasQuoted = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];

    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          // Escaped quote inside a quoted field.
          current += '"';
          i += 1;
        } else {
          // Closing quote.
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else if (char === '"') {
      // Opening quote for this field.
      inQuotes = true;
      wasQuoted = true;
    } else if (char === ",") {
      fields.push(wasQuoted ? current : current.trim());
      current = "";
      wasQuoted = false;
    } else {
      current += char;
    }
  }

  fields.push(wasQuoted ? current : current.trim());

  return fields;
}

/**
 * Parse header-less CSV text into a {@link ParseResult}.
 *
 * Treats each non-blank line as a row whose columns, in order, are name,
 * address, and the optional latitude and longitude.
 *
 * Latitude/longitude are OPTIONAL: the Places API geocodes a coordinate from
 * `address` when they are missing. A row is valid when it has at least two
 * columns and a non-empty `address` (required so the row is geocodable). Its
 * coordinate rules are:
 *   - both lat and lon present, numeric, and in range  -> row keeps that coordinate;
 *   - both lat and lon omitted or blank                -> row is valid with lat/lon = null (geocoded from address);
 *   - anything else (only one supplied, non-numeric, or out-of-range)
 *                                                       -> row is excluded as malformed.
 *
 * @param {string} text - The raw CSV file contents.
 * @returns {ParseResult} The parse result.
 */
export function parseCSV(text) {
  const valid = [];
  let total = 0;

  const raw = text == null ? "" : String(text);
  const lines = raw.split(/\r\n|\r|\n/);

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    // Skip fully blank lines; they are not counted as parseable rows.
    if (line.trim() === "") {
      continue;
    }

    total += 1;

    const columns = splitCsvLine(line);

    // Malformed: need at least a name and an address column.
    if (columns.length < 2) {
      continue;
    }

    const name = columns[0];
    const address = columns[1];
    const latRaw = columns.length > 2 ? columns[2] : "";
    const lonRaw = columns.length > 3 ? columns[3] : "";

    // Address is required so the Places API can geocode rows without a coordinate.
    if (String(address).trim() === "") {
      continue;
    }

    const latBlank = String(latRaw).trim() === "";
    const lonBlank = String(lonRaw).trim() === "";

    let lat = null;
    let lon = null;

    if (latBlank && lonBlank) {
      // No coordinate supplied: leave null; the address is geocoded server-side.
      lat = null;
      lon = null;
    } else {
      // A coordinate was supplied: both parts must be present, numeric, and in range.
      lat = parseCoordinate(latRaw);
      lon = parseCoordinate(lonRaw);

      if (lat === null || lon === null) {
        continue;
      }

      if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
        continue;
      }
    }

    valid.push({ name, address, lat, lon });
  }

  const validCount = valid.length;
  const excludedCount = total - validCount;

  let error = null;
  if (validCount === 0) {
    error = "File is empty or contains no valid rows.";
  } else if (validCount > CSV_ROW_CAP) {
    error = "CSV exceeds the maximum of 10,000 rows.";
  }

  return {
    valid,
    total,
    validCount,
    excludedCount,
    error,
  };
}

/**
 * Parse a single coordinate field to a finite number, or null when it is not a
 * valid numeric value. Rejects empty/whitespace-only strings and non-numeric
 * text (e.g. "12abc").
 *
 * @param {string} value - The raw coordinate field.
 * @returns {number|null} The parsed number, or null when invalid.
 */
function parseCoordinate(value) {
  if (value == null) {
    return null;
  }

  const trimmed = String(value).trim();
  if (trimmed === "") {
    return null;
  }

  const num = Number(trimmed);
  if (!Number.isFinite(num)) {
    return null;
  }

  return num;
}

/**
 * Map valid CSV rows to square-polygon (Radius mode) preview `Feature`s, capped
 * at `cap` features and preserving the original file order.
 *
 * The `rows` are sliced to `cap` first (mirroring {@link toPreviewFeatures}'s
 * ordering/cap semantics). Rows without a coordinate (lat/lon omitted, to be
 * geocoded server-side from the address) are skipped since they cannot be
 * drawn on the map; they remain valid CSV rows and are still uploaded. Each
 * remaining row's coordinate is passed to {@link squarePolygonFor}, and rows
 * whose geometry is rejected (a `null` result — e.g. a bad `radiusValue`/`unit`)
 * are skipped too.
 *
 * Each surviving geometry is wrapped as a GeoJSON `Feature` whose `properties`
 * shape is identical to {@link toPreviewFeatures} (`name`, `address`,
 * `_source: 'csv'`) so downstream marker/labeling code is agnostic to the
 * Place_Type.
 *
 * @param {CsvRow[]} rows - Valid CSV rows.
 * @param {number} radiusValue - A finite radius greater than 0.
 * @param {'miles'|'kilometers'} unit - The distance unit for `radiusValue`.
 * @param {number} [cap=CSV_PREVIEW_CAP] - Maximum number of features to emit.
 * @returns {Array<Object>} At most `cap` square `Polygon` `Feature`s.
 */
export function toRadiusPreviewFeatures(
  rows,
  radiusValue,
  unit,
  cap = CSV_PREVIEW_CAP
) {
  const source = Array.isArray(rows) ? rows : [];

  const features = [];
  for (const row of source.slice(0, cap)) {
    // Rows without a coordinate (geocoded server-side from address) cannot be
    // previewed on the map; skip them here. They remain valid CSV rows.
    if (row.lat === null || row.lat === undefined || row.lon === null || row.lon === undefined) {
      continue;
    }

    const geometry = squarePolygonFor(row.lon, row.lat, radiusValue, unit);
    if (geometry === null) {
      continue;
    }

    features.push({
      type: "Feature",
      properties: {
        name: row.name,
        address: row.address,
        _source: "csv",
      },
      geometry,
    });
  }

  return features;
}

/**
 * Map valid CSV rows to GeoJSON `Point` features for map/tab preview, capped at
 * `cap` features.
 *
 * NOTE: Implementation is added in a later task (Task 3.1).
 *
 * Rows without a coordinate (lat/lon omitted, to be geocoded server-side from
 * the address) cannot be placed on the map and are skipped here; they remain
 * valid CSV rows and are still uploaded.
 *
 * @param {CsvRow[]} rows - Valid CSV rows.
 * @param {number} [cap=CSV_PREVIEW_CAP] - Maximum number of features to emit.
 * @returns {Array<Object>} At most `cap` GeoJSON `Point` features.
 */
export function toPreviewFeatures(rows, cap = CSV_PREVIEW_CAP) {
  const source = Array.isArray(rows) ? rows : [];

  return source
    .slice(0, cap)
    // Rows without a coordinate (geocoded server-side from address) cannot be
    // previewed on the map; skip them here. They remain valid CSV rows.
    .filter(
      (row) =>
        row.lat !== null &&
        row.lat !== undefined &&
        row.lon !== null &&
        row.lon !== undefined
    )
    .map((row) => ({
      type: "Feature",
      properties: {
        name: row.name,
        address: row.address,
        _source: "csv",
      },
      geometry: {
        type: "Point",
        coordinates: [row.lon, row.lat],
      },
    }));
}

/**
 * Build the multipart form body for the raw CSV file upload request.
 *
 * NOTE: Implementation is added in a later task (Task 3.2).
 *
 * @param {File} file - The raw CSV file.
 * @returns {FormData} A FormData with the file part appended.
 */
export function buildFileUploadForm(file) {
  const form = new FormData();
  form.append("file", file);
  return form;
}

/**
 * Build the client portion of the `/v2/places/library` registration request
 * body. Tenant/account/user/country are resolved server-side and are never
 * emitted here.
 *
 * NOTE: Implementation is added in a later task (Task 3.2).
 *
 * @param {Object} entry - The committed CSV entry.
 * @param {string} companyId - The active company id.
 * @returns {Object} The library registration request body.
 */
export function buildLibraryBody(entry, companyId) {
  const fileName = entry.uploadPath
    ? entry.uploadPath.split("/").pop()
    : entry.fileName;

  // A Radius upload carries a positive radius value and its unit. Forward them
  // as radiusValue/radiusUnit; the backend normalizes the unit and converts
  // kilometers to miles before handing a single `radius` to the Places API.
  const isRadius =
    entry.placeType === "Radius" &&
    entry.radiusValue != null &&
    Number(entry.radiusValue) > 0;

  return {
    companyId,
    type: "places",
    brand: entry.brand,
    category: entry.category,
    name: entry.fileName,
    genre: "realtime",
    description: entry.fileName,
    fileName,
    uploadPath: entry.uploadPath,
    createList: true,
    ...(isRadius
      ? { radiusValue: Number(entry.radiusValue), radiusUnit: entry.radiusUnit }
      : {}),
  };
}
